/**
 * BlockTile — one block rendered inside the calendar grid.
 *
 * Layout is height-aware:
 *   - compact (< 34px):  one line — "9A · Draft the Agenda"
 *   - medium (34–64px):  two lines — time on row 1, title on row 2 (clamp 2)
 *   - tall (≥ 64px):     adds output_intent below the title (clamp 2)
 *
 * Interaction: mousedown is forwarded to the calendar so it can start a
 * potential drag. The calendar handles the "click vs drag" split itself —
 * mouseup without crossing the threshold becomes a select; mouseup past
 * the threshold becomes a reschedule. Keyboard (Enter / Space) still
 * selects the block directly.
 */

import { useStudioStore } from '@/stores/useStudioStore'
import type { StudioBlock } from '@/lib/studio'
import { formatTimeLabel } from '@/features/studio/lib/time'

interface Props {
  block: StudioBlock
  topPx: number
  heightPx: number
  universeColor: string | null
  active: boolean
  /** Set when this tile is currently being dragged — fades the original so the ghost reads as the live position. */
  isDragging?: boolean
  /** Forwarded to the calendar's drag controller. The calendar decides whether to treat it as click or drag. */
  onMouseDownBlock?: (block: StudioBlock, e: React.MouseEvent) => void
}

const STATUS_TINT: Record<string, string> = {
  completed: 'bg-blue-900/35 hover:bg-blue-900/55 border-blue-700/50',
  in_progress: 'bg-yellow-900/35 hover:bg-yellow-900/55 border-yellow-700/50',
  scheduled: 'bg-emerald-900/30 hover:bg-emerald-900/50 border-emerald-700/50',
  pending: 'bg-neutral-800/60 hover:bg-neutral-700/60 border-neutral-700',
  overdue: 'bg-red-900/35 hover:bg-red-900/55 border-red-700/50',
  missing: 'bg-orange-900/35 hover:bg-orange-900/55 border-orange-700/50',
}

export function BlockTile({
  block, topPx, heightPx, universeColor, active, isDragging, onMouseDownBlock,
}: Props) {
  const setActiveBlock = useStudioStore((s) => s.setActiveBlock)

  const startsAt = block.starts_at ? new Date(block.starts_at) : null
  const tint = STATUS_TINT[block.status] || STATUS_TINT.pending
  const timeLabel = startsAt ? formatTimeLabel(startsAt) : ''

  const isCompact = heightPx < 34
  const isTall = heightPx >= 64

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseDown={(e) => onMouseDownBlock?.(block, e)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setActiveBlock(block.id)
        }
      }}
      className={
        'absolute left-1 right-1 rounded-sm border overflow-hidden select-none transition ' +
        (block.all_day ? 'cursor-pointer ' : 'cursor-grab active:cursor-grabbing ') +
        tint +
        (active ? ' ring-1 ring-neutral-300' : '') +
        (isDragging ? ' opacity-25 pointer-events-none' : '')
      }
      style={{ top: topPx, height: Math.max(heightPx, 22) }}
      title={`${block.title}${block.output_intent ? ` — ${block.output_intent}` : ''}`}
    >
      {/* Universe accent stripe */}
      <div
        className="absolute top-0 bottom-0 left-0 w-[3px]"
        style={{ backgroundColor: universeColor || 'transparent' }}
      />

      <div className="h-full pl-[7px] pr-1.5 py-0.5 flex flex-col justify-start overflow-hidden">
        {isCompact ? (
          <div className="flex items-baseline gap-1.5 leading-tight">
            {timeLabel && (
              <span className="text-[9px] tabular-nums text-neutral-400 font-light shrink-0">
                {timeLabel}
                {block.fast ? ' ⚡' : ''}
              </span>
            )}
            <span className="text-[11px] text-neutral-100 truncate font-light">
              {block.title}
            </span>
          </div>
        ) : (
          <>
            {timeLabel && (
              <div className="text-[9px] tabular-nums text-neutral-400 font-light leading-tight">
                {timeLabel}
                {block.fast ? ' ⚡' : ''}
              </div>
            )}
            <div className="text-[11px] font-light text-neutral-100 leading-snug line-clamp-2 mt-0.5">
              {block.title}
            </div>
            {isTall && block.output_intent && (
              <div className="text-[9px] text-neutral-400 leading-snug line-clamp-2 mt-1">
                {block.output_intent}
              </div>
            )}
          </>
        )}
      </div>

      {block.score === 1 && (
        <div className="absolute top-0.5 right-1 text-[9px] text-emerald-400 leading-none">✓</div>
      )}
      {block.score === 0 && (
        <div className="absolute top-0.5 right-1 text-[9px] text-red-400 leading-none">✗</div>
      )}
    </div>
  )
}
